import { useLoaders, useSaveActions } from "../../common"
import { TableName } from "../../../tables"
import { Expense, PaymentCategory, PaymentOrder, PaymentOrderStatus, Ticket, TransactionDestinationType, TransactionSourceType } from "../state/types"
import { define } from "../../../utils/typeUtils"
import { usePaymentOrderLoaders } from "../loaders/paymentOrderLoaders"
import { useCashFundActions } from "./cashFundActions"
import { useCheckActions } from "./checkActions"
import { useTicketLoaders } from "../loaders/ticketLoaders"
import { useTicketActions } from "./ticketActions_NEW"
import { useSystemStateHooks } from "../../system"
import { useTicketHooks } from "../hooks/ticketHooks"
import { PurchaseOrder } from "../../purchases"
import { PaymentStatus } from "../../operations"
import { fileHelpers, useFileActions } from "../../../features/files"

export const usePaymentOrderActions = () => {
    const { paymentReceiptSubPath } = fileHelpers
    const systemHooks = useSystemStateHooks()
    const { getParentPendingAmount } = useTicketHooks()

    const loaders = useLoaders()
    const paymentOrderLoaders = usePaymentOrderLoaders()
    const ticketLoaders = useTicketLoaders()
    
    const saveActions = useSaveActions()
    const { reserveInCashFund, transferMoney } = useCashFundActions()
    const { endorseCheck, payWithCheck } = useCheckActions()
    const { saveTicket } = useTicketActions()
    const { saveFile } = useFileActions()

    return (companyId = systemHooks.companyId) => {
        const savePaymentOrder = (paymentOrder: PaymentOrder) => {
            const { save } = saveActions(companyId)
            return save(TableName.PAYMENT_ORDERS, paymentOrder) as Promise<PaymentOrder>
        }
        
        const createPaymentOrder = async (paymentOrder: PaymentOrder) => {
            const savedPaymentOrder = await savePaymentOrder(paymentOrder)

            paymentOrder.paymentItems.forEach(async paymentItem => {
                const { sourceId, amount } = paymentItem
                if (paymentItem.paymentCategory === PaymentCategory.CASH_FUND) {
                    await reserveInCashFund(sourceId, amount)
                } else if (paymentItem.paymentCategory === PaymentCategory.CHECK) {
                    await payWithCheck(TransactionSourceType.CHECK, TransactionDestinationType.PAYMENT_ORDER, amount, sourceId, savedPaymentOrder.id)
                }
            })

            paymentOrder.ticketItems.forEach(async ticketItem => {
                const { loadTicket } = ticketLoaders(companyId)
                const loadedTicket = define(await loadTicket(ticketItem.ticketId, true))
                
                const updatedTicket: Ticket = {
                    ...loadedTicket,
                    payedAmount: loadedTicket.payedAmount + ticketItem.payedAmount
                }
                await saveTicket(updatedTicket)
            })

            return savedPaymentOrder
        }

        const confirmPaymentOrder = async (
            paymentOrderId: string,
            paymentDate: Date,
            receiptFile?: File
        ) => {
            const { loadPaymentOrder } = paymentOrderLoaders(companyId)
            const loadedPaymentOrder = await loadPaymentOrder(paymentOrderId, true)

            const paymentOrder: PaymentOrder = {
                ...loadedPaymentOrder!,
                status: PaymentOrderStatus.PAYED
            }

            if (receiptFile) {
                const ext = fileHelpers.getFileExtension(receiptFile)
                const fileSubPath = paymentReceiptSubPath(loadedPaymentOrder!, ext)
                const receiptUrl = await saveFile(fileSubPath, receiptFile)
                paymentOrder!.receiptUrl = receiptUrl
            }

            paymentOrder.paymentItems = loadedPaymentOrder!.paymentItems.map(paymentItem => ({
                ...paymentItem,
                paymentDate: paymentDate.toISOString()
            }))

            const savedPaymentOrder = await savePaymentOrder(paymentOrder)

            savedPaymentOrder.paymentItems.forEach(async paymentItem => {
                if (paymentItem.paymentCategory === PaymentCategory.CASH_FUND) {
                    await transferMoney(TransactionSourceType.CASH_FUND, TransactionDestinationType.PAYMENT_ORDER, paymentItem.amount, paymentItem.sourceId, savedPaymentOrder.id, paymentDate, true)
                } else if (paymentItem.paymentCategory === PaymentCategory.CHECK) {
                    await endorseCheck(paymentItem.sourceId)
                }
            })

            savedPaymentOrder.ticketItems.forEach(async ticketItem => {
                const { loadTicket } = ticketLoaders(companyId)
                const loadedTicket = define(await loadTicket(ticketItem.ticketId, true))
                const { parentTable, parentId } = loadedTicket
                const { load } = loaders(companyId)
                const parent = define(await load(parentTable as TableName, parentId, true)) as PurchaseOrder | Expense
                const pendingAmount = await getParentPendingAmount(parentId)
                const paymentStatus = pendingAmount === 0 ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED

                const updatedParent = {
                    ...parent,
                    paymentStatus
                }
                const { save } = saveActions(companyId)
                await save(parentTable as TableName, updatedParent)
            })

            return savedPaymentOrder
        }

        const uploadPaymentReceipt = async (
            paymentOrderId: string,
            receiptFile: File
        ) => {
            const { loadPaymentOrder } = paymentOrderLoaders()
            const loadedPaymentOrder = await loadPaymentOrder(paymentOrderId, true)
    
            const ext = fileHelpers.getFileExtension(receiptFile)
            const fileSubPath = paymentReceiptSubPath(loadedPaymentOrder!, ext)
            const receiptUrl = await saveFile(fileSubPath, receiptFile)
            const updatedPaymentOrder: PaymentOrder = {
                ...loadedPaymentOrder!,
                receiptUrl: receiptUrl
            }
            
            return savePaymentOrder(updatedPaymentOrder)
        }

        return {
            savePaymentOrder,
            createPaymentOrder,
            confirmPaymentOrder,
            uploadPaymentReceipt
        }
    }
}
